'use client'
import { authService } from 'api/auth'
import { useAuthStoreHasHydrated, useAuthUser } from 'lib/auth'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

function GridPattern() {
  return (
    <div className='absolute inset-0 overflow-hidden pointer-events-none'>
      <div className='absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]' />
      <div className='absolute inset-0 bg-gradient-to-t from-black via-transparent to-black' />
    </div>
  )
}

function GlowOrb({ className }: { className?: string }) {
  // Note: blur-[100px] was removed as it causes severe performance issues
  // and browser freezes on mobile Safari. Using a gradient background instead.
  return (
    <div
      className={`absolute rounded-full opacity-20 ${className}`}
      style={{
        background: 'radial-gradient(circle, currentColor 0%, transparent 70%)'
      }}
    />
  )
}

export default function LoginPage() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const searchParams = useSearchParams()
  const from = searchParams.get('from')
  const user = useAuthUser()
  const hasHydrated = useAuthStoreHasHydrated()

  useEffect(() => {
    if (hasHydrated && user) {
      // Prevent redirect loop by not redirecting to /login
      if (typeof from === 'string' && !from.startsWith('/login')) {
        router.replace(from)
      } else {
        router.replace('/console/dashboard')
      }
    }
  }, [hasHydrated, user, from, router])

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!email || !password) {
        toast.error('Please enter your email and password')
        return
      }
      setLoading(true)
      authService
        .login(email, password)
        .then(() => {
          // Prevent redirect loop by not redirecting to /login
          if (typeof from === 'string' && !from.startsWith('/login')) {
            router.replace(from)
          } else {
            router.replace('/console/dashboard')
          }
        })
        .catch(() => {
          toast.error('Invalid email or password')
        })
        .finally(() => {
          setLoading(false)
        })
    },
    [router, from, email, password]
  )

  return (
    <div className='min-h-screen bg-black flex items-center justify-center relative overflow-hidden'>
      <GridPattern />
      <GlowOrb className='w-[600px] h-[600px] bg-blue-500 -top-40 -left-40' />
      <GlowOrb className='w-[400px] h-[400px] bg-purple-500 -bottom-20 -right-20' />

      <div className='relative z-10 w-full max-w-md px-6'>
        {/* Header */}
        <div className='text-center mb-10'>
          <Link href='/' className='inline-block mb-8'>
            <span className='text-2xl font-bold text-white tracking-tight'>Lunara</span>
          </Link>
          <h1 className='text-3xl font-bold text-white mb-2'>Welcome back</h1>
          <p className='text-zinc-400'>Sign in to your account to continue</p>
        </div>

        {/* Form Card */}
        {/* Note: backdrop-blur-xl was removed as it causes browser freezes on mobile Safari */}
        <div className='p-8 rounded-2xl border border-white/10 bg-zinc-900/80'>
          <form onSubmit={onSubmit} className='space-y-6'>
            <div>
              <label htmlFor='email' className='block text-sm font-medium text-zinc-300 mb-2'>
                Email address
              </label>
              <input
                id='email'
                type='email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all'
                placeholder='you@example.com'
                required
              />
            </div>

            <div>
              <label htmlFor='password' className='block text-sm font-medium text-zinc-300 mb-2'>
                Password
              </label>
              <input
                id='password'
                type='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className='w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all'
                placeholder='Enter your password'
                required
              />
            </div>

            <div className='flex items-center justify-end'>
              <Link
                href='/forgot-password'
                className='text-sm text-zinc-400 hover:text-white transition-colors'
              >
                Forgot password?
              </Link>
            </div>

            <button
              type='submit'
              disabled={loading}
              className='w-full py-3 px-4 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
            >
              {loading ? (
                <span className='flex items-center justify-center gap-2'>
                  <svg className='animate-spin h-4 w-4' viewBox='0 0 24 24'>
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                      fill='none'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className='mt-8 text-center'>
          <p className='text-sm text-zinc-500'>
            Don&apos;t have an account?{' '}
            <Link href='/' className='text-white hover:underline'>
              Contact us
            </Link>
          </p>
        </div>

        {/* Back to home */}
        <div className='mt-6 text-center'>
          <Link
            href='/'
            className='inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors'
          >
            <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
            </svg>
            Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}
